/**
 * Capture / restore each material's opacity under a subtree (by material.uuid).
 * Used when timeline playback resets state: Groups have no root material, so a
 * single `opacity: 1` snapshot must not be applied to every child mesh.
 */

export function captureSubtreeMaterialOpacity(root) {
  const snapshot = {};
  if (!root) return snapshot;

  root.traverse((obj) => {
    if (!obj.material) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((mat) => {
      if (!mat || !mat.uuid || snapshot[mat.uuid] !== undefined) return;
      snapshot[mat.uuid] = {
        opacity: mat.opacity,
        transparent: mat.transparent,
        ...(mat.uniforms && mat.uniforms.alpha
          ? { alpha: mat.uniforms.alpha.value }
          : {}),
      };
    });
  });

  return snapshot;
}

export function restoreSubtreeMaterialOpacity(root, snapshot) {
  if (!root || !snapshot || typeof snapshot !== 'object') return;
  const uuids = Object.keys(snapshot);
  if (uuids.length === 0) return;

  root.traverse((obj) => {
    if (!obj.material) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((mat) => {
      if (!mat || !mat.uuid) return;
      const rec = snapshot[mat.uuid];
      if (!rec) return;
      mat.opacity = rec.opacity;
      mat.transparent = rec.transparent;
      if (rec.alpha !== undefined && mat.uniforms && mat.uniforms.alpha) {
        mat.uniforms.alpha.value = rec.alpha;
      }
      mat.needsUpdate = true;
    });
  });
}

/**
 * Single configured opacity for particle systems (clouds store `userData.opacity`;
 * foliage uses `userData.material_settings.opacity`).
 */
export function getParticleConfiguredOpacity(obj) {
  if (!obj?.userData) return undefined;
  if (obj.userData.opacity !== undefined) return Number(obj.userData.opacity);
  if (obj.userData.material_settings?.opacity !== undefined) {
    return Number(obj.userData.material_settings.opacity);
  }
  return undefined;
}

/**
 * Restore materials after animation playback (fade, etc.) on a single object subtree.
 * Prefers a captured per-material snapshot, then animation originalMaterials list.
 */
export function restoreAnimatedObjectMaterials(obj) {
  if (!obj) return false;

  if (
    obj.userData?.materialOpacitySnapshot &&
    Object.keys(obj.userData.materialOpacitySnapshot).length > 0
  ) {
    restoreSubtreeMaterialOpacity(obj, obj.userData.materialOpacitySnapshot);
    return true;
  }

  if (Array.isArray(obj.userData?.originalMaterials)) {
    const configuredOp = getParticleConfiguredOpacity(obj);
    obj.userData.originalMaterials.forEach(
      ({ mat, opacity, transparent, alpha }) => {
        if (!mat) return;
        const targetOpacity =
          configuredOp !== undefined ? configuredOp : opacity;
        mat.opacity = targetOpacity;
        mat.transparent = transparent;
        if (mat.uniforms?.alpha) {
          mat.uniforms.alpha.value =
            alpha !== undefined ? alpha : targetOpacity;
        }
        mat.needsUpdate = true;
      }
    );
    return true;
  }

  return false;
}
